<template>
	<div class="nob-wrapper">
		<!-- New Onboarding — dropdown with onboarding types -->
		<div class="dropdown">
			<button class="nob-btn dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
				<font-awesome-icon :icon="['fas', 'plus']" />New Onboarding
			</button>
			<ul class="dropdown-menu dropdown-menu-end nob-type-menu">
				<li class="nob-type-menu-header">Choose onboarding type</li>
				<li v-for="type in onboardingTypes" :key="'type-' + type.id">
					<button class="dropdown-item nob-type-item" type="button" @click="startNewOnboarding(type)">
						<span class="nob-type-name">{{ type.name }}</span>
						<span v-if="type.description" class="nob-type-desc">{{ type.description }}</span>
					</button>
				</li>
				<li v-if="onboardingTypes.length === 0" class="nob-type-empty">
					<font-awesome-icon :icon="['fas', 'spinner']" class="fa-spin me-2" v-if="loadingTypes" />
					<span v-else>No onboarding types found</span>
				</li>
			</ul>
		</div>

		<!-- Modal Add Onboarding -->
		<div class="modal fade os-modal" ref="modalEl" tabindex="-1" aria-hidden="true">
			<div class="modal-dialog">
				<div class="modal-content">
					<form @submit.prevent="addOnboarding">
						<div class="modal-header">
							<div>
								<h5 class="modal-title os-modal-title">New Onboarding</h5>
								<p class="os-modal-sub">Starts a new onboarding — the type decides which steps and fields you'll go through on the call.</p>
							</div>
							<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						</div>
						<div class="modal-body">
							<div class="os-form-group">
								<ClientsSearch :selected="null" v-on:setClient="setClient" />
								<p class="os-hint">Optional — links the onboarding to an MC2 client and prefills the name and client data.</p>
							</div>
							<div class="os-form-group">
								<label class="os-form-label">Name</label>
								<input class="form-control" type="text" required placeholder="e.g. Town of York" v-model="newOnboarding.name" />
							</div>
							<div class="os-form-group" v-if="selectedType">
								<label class="os-form-label">Onboarding Type</label>
								<div class="os-type-selected">
									<i class="fas fa-check-circle os-type-selected-icon"></i>
									<span class="os-type-texts">
										<span class="os-type-selected-name">{{ selectedType.name }}</span>
										<span v-if="selectedType.description" class="os-type-selected-desc">{{ selectedType.description }}</span>
									</span>
								</div>
								<p class="os-hint">Picked from the New Onboarding dropdown — close this dialog to choose a different type.</p>
							</div>
							<div class="os-form-group mb-0">
								<label class="os-form-label">Description</label>
								<textarea class="form-control" rows="2" placeholder="Optional internal note" v-model="newOnboarding.description" />
							</div>
						</div>
						<div class="modal-footer">
							<button type="button" class="os-btn" data-bs-dismiss="modal">Cancel</button>
							<button type="submit" class="os-btn os-btn--primary" :disabled="creating || !newOnboarding.name || !newOnboarding.onboarding_type_id">
								<font-awesome-icon v-if="creating" :icon="['fas', 'spinner']" class="fa-spin" />
								{{ creating ? 'Creating…' : 'Create Onboarding' }}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { Modal } from 'bootstrap'
import restApi from '@/api'
import ClientsSearch from '@/components/clients/ClientsSearch.vue'

export default {
	name: 'NewOnboardingButton',
	components: { ClientsSearch },
	data() {
		return {
			modal: null,
			loadingTypes: false,
			creating: false,
			onboardingTypes: [],
			newOnboarding: {
				name: '',
				description: '',
				client_id: null,
				municipality_id: null,
				onboarding_type_id: null,
			},
		}
	},
	computed: {
		selectedType() {
			return this.onboardingTypes.find(type => type.id === this.newOnboarding.onboarding_type_id) || null
		},
	},
	mounted() {
		this.modal = new Modal(this.$refs.modalEl)
	},
	beforeDestroy() {
		if (this.modal) this.modal.dispose()
	},
	created() {
		this.getOnboardingTypes()
	},
	methods: {
		getOnboardingTypes() {
			this.loadingTypes = true
			restApi
				.get('/onboarding-type')
				.then(({ data }) => {
					this.onboardingTypes = data
					this.loadingTypes = false
				})
				.catch(error => {
					console.log(error)
					this.loadingTypes = false
				})
		},
		// From the dropdown — type preselected
		startNewOnboarding(type) {
			this.newOnboarding.onboarding_type_id = type.id
			this.modal.show()
		},
		addOnboarding() {
			this.creating = true
			restApi
				.post('/onboarding', this.newOnboarding)
				.then(({ data }) => {
					this.creating = false
					this.modal.hide()
					this.$root.$emit('onboarding-created')
					this.$emit('created', data)
					this.$router.push('/onboarding/progress/' + data.id)
				})
				.catch(error => {
					console.log(error)
					this.creating = false
					this.$swal.fire({ icon: 'error', title: 'Creating the onboarding failed', text: String(error) })
				})
		},
		setClient(client) {
			if (client) {
				this.newOnboarding.name = client.name
				this.newOnboarding.client_id = client.id
			}
		},
	},
}
</script>

<style scoped>
.nob-wrapper {
	display: inline-block;
}

/* New Onboarding dropdown button — Juniper pill, blue filled (hqa-generate design) */
.nob-btn {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 7px 12px;
	border: none;
	border-radius: 999px;
	background: #2b6cff;
	color: #ffffff;
	font-size: 12px;
	font-weight: 600;
	cursor: pointer;
	transition: background 0.15s ease;
	white-space: nowrap;
}

.nob-btn:hover,
.nob-btn:focus,
.nob-btn:active {
	background: #2057db;
	color: #ffffff;
}

.nob-type-menu {
	min-width: 260px;
	border-radius: 10px;
	border: 1px solid #e5e7eb;
	box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
	padding: 6px;
}

.nob-type-menu-header {
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: #94a3b8;
	padding: 8px 12px 6px;
	user-select: none;
}

.nob-type-item {
	display: flex;
	flex-direction: column;
	gap: 2px;
	padding: 8px 12px;
	border-radius: 7px;
	white-space: normal;
}

.nob-type-item:hover {
	background: #f3f7ff;
}

.nob-type-name {
	font-size: 13px;
	font-weight: 600;
	color: #111827;
}

.nob-type-desc {
	font-size: 12px;
	color: #6b7280;
	line-height: 1.35;
}

.nob-type-empty {
	padding: 10px 12px;
	font-size: 13px;
	color: #6b7280;
}

/* Modal — flat Juniper look */
.os-modal ::v-deep .modal-content {
	border: 1px solid #e6e8ec;
	border-radius: 14px;
}

.os-modal .modal-header {
	border-bottom: none;
	padding-bottom: 4px;
	align-items: flex-start;
}

.os-modal .modal-footer {
	border-top: none;
	padding-top: 4px;
	gap: 4px;
}

.os-modal-title {
	font-size: 15px;
	font-weight: 700;
	color: #111827;
	margin: 0;
}

.os-modal-sub {
	font-size: 12px;
	color: #8a909c;
	margin: 3px 0 0;
	line-height: 1.4;
}

.os-form-group {
	margin-bottom: 14px;
}

.os-form-label {
	font-size: 12px;
	font-weight: 600;
	color: #374151;
	margin-bottom: 4px;
	display: block;
}

.os-hint {
	font-size: 12px;
	color: #8a909c;
	line-height: 1.45;
	margin: 5px 0 0;
}

/* Selected type — read-only display (picked in the dropdown) */
.os-type-selected {
	display: flex;
	align-items: flex-start;
	gap: 10px;
	padding: 10px 12px;
	border: 1px solid #dbeafe;
	border-radius: 10px;
	background: #f3f7ff;
}

.os-type-selected-icon {
	color: #2b6cff;
	font-size: 13px;
	margin-top: 2px;
	flex-shrink: 0;
}

.os-type-texts {
	display: flex;
	flex-direction: column;
	gap: 1px;
	min-width: 0;
}

.os-type-selected-name {
	font-size: 13px;
	font-weight: 600;
	color: #1d4ed8;
}

.os-type-selected-desc {
	font-size: 11.5px;
	color: #6b7280;
	line-height: 1.35;
}

/* Flat buttons — cd-btn design */
.os-btn {
	display: inline-flex;
	align-items: center;
	gap: 0.4rem;
	padding: 0.45rem 0.9rem;
	border: 1px solid #e6e8ec;
	border-radius: 9px;
	font-size: 0.85rem;
	font-weight: 600;
	background: transparent;
	color: #2b303b;
	cursor: pointer;
	transition: all 0.12s ease;
}

.os-btn:hover:not(:disabled) {
	background: #f3f5f9;
}

.os-btn:disabled {
	opacity: 0.55;
	cursor: not-allowed;
}

.os-btn--primary {
	color: #2b6cff;
	border-color: #b6cfff;
}

.os-btn--primary:hover:not(:disabled) {
	background: #ebf2ff;
	border-color: #8fb4ff;
	color: #2b6cff;
}
</style>
